import { useCallback } from 'react';
import { useBoundaryIsCustom } from '~/store/boundaryGroups/boundaryGroups.selectors';
import { useActiveBoundaryTerritoryGroupsSelector } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.selectors';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useIsMapEditDisabled } from '~/store/selectors/useMapInfoSelectors';
import { createBoundaryTerritoryGroupToggleUpdater } from './boundaryTerritoryGroupUpdates.helpers';
import { CommonSettingsComponent } from './commonSettingsComponent';

type CommonSettingsContainerProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
}>;

export const CommonSettingsContainer = ({
  boundaryTerritoryGroup,
  onBoundaryTerritoryGroupChange,
}: CommonSettingsContainerProps) => {
  const isEditModeDisabled = useIsMapEditDisabled();
  const activeBoundaryTerritoryGroups = useActiveBoundaryTerritoryGroupsSelector();
  const isBoundaryCustom = useBoundaryIsCustom(boundaryTerritoryGroup.boundaryGroupId);
  const isConnectedBTG = !!boundaryTerritoryGroup.settings.connectedEntityData;

  const allowToggleHideFillWhenInactive = activeBoundaryTerritoryGroups.length > 1
    && (!isBoundaryCustom || !!boundaryTerritoryGroup.settings.boundaryTerritories.length)
    && !isConnectedBTG;
  const allowToggleHideAreasWithoutData = !isEditModeDisabled && !isConnectedBTG;
  const allowToggleIgnoreFilters = !isEditModeDisabled && !isConnectedBTG;
  const allowToggleBoundariesAsTerritories = !isEditModeDisabled
    && boundaryTerritoryGroup.settings.boundaryTerritories.length && !isConnectedBTG;

  const onToggleHideFillWhenInactive = useCallback(() => {
    const updater = createBoundaryTerritoryGroupToggleUpdater(boundaryTerritoryGroup, 'hideFillWhenInactive');
    onBoundaryTerritoryGroupChange(updater());
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const onToggleDisableMapInteraction = useCallback(() => {
    const updater = createBoundaryTerritoryGroupToggleUpdater(boundaryTerritoryGroup, 'disableMapInteraction');
    onBoundaryTerritoryGroupChange(updater());
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const onToggleHideAreasWithoutData = useCallback(() => {
    const updater = createBoundaryTerritoryGroupToggleUpdater(boundaryTerritoryGroup, 'hideWithoutData');
    onBoundaryTerritoryGroupChange(updater());
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const onToggleIgnoreFilters = useCallback(() => {
    const updater = createBoundaryTerritoryGroupToggleUpdater(boundaryTerritoryGroup, 'ignoreFilters');
    onBoundaryTerritoryGroupChange(updater());
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  const handleToggleBoundariesAsTerritories = useCallback(() => {
    const updater = createBoundaryTerritoryGroupToggleUpdater(boundaryTerritoryGroup, 'boundariesAsTerritories');
    onBoundaryTerritoryGroupChange(updater());
  }, [boundaryTerritoryGroup, onBoundaryTerritoryGroupChange]);

  return (
    <CommonSettingsComponent
      onBoundaryTerritoryGroupChange={onBoundaryTerritoryGroupChange}
      boundaryTerritoryGroup={boundaryTerritoryGroup}
      isBoundaryTerritoryGroupCustom={isBoundaryCustom}
      togglesProps={{
        boundariesAsTerritories: boundaryTerritoryGroup.settings.boundariesAsTerritories,
        hideFillWhenInactive: boundaryTerritoryGroup.settings.hideFillWhenInactive,
        hideWithoutData: boundaryTerritoryGroup.settings.hideWithoutData,
        ignoreFilters: boundaryTerritoryGroup.settings.ignoreFilters,
        disableMapInteraction: boundaryTerritoryGroup.settings.disableMapInteraction,
        onToggleHideFillWhenInactive: allowToggleHideFillWhenInactive ? onToggleHideFillWhenInactive : undefined,
        onToggleHideAreasWithoutData: allowToggleHideAreasWithoutData ? onToggleHideAreasWithoutData : undefined,
        onToggleIgnoreFilters: allowToggleIgnoreFilters ? onToggleIgnoreFilters : undefined,
        onToggleBoundariesAsTerritories: allowToggleBoundariesAsTerritories ? handleToggleBoundariesAsTerritories : undefined,
        onToggleDisableMapInteraction,
      }}
    />
  );
};
